import { getCurrentIdToken } from '../auth/authService';
import {
  generateHandbookRequestSchema,
  generateHandbookResponseSchema,
  entitlementStatusResponseSchema,
  handbookAnswersSchema,
  paymentIntentRequestSchema,
  paymentIntentResponseSchema,
  resendHandbookRequestSchema,
  resendHandbookResponseSchema,
  type GenerateHandbookResponse,
  type HandbookAnswers,
  type EntitlementStatusResponse,
  type GenerateHandbookRequest,
  type PaymentIntentRequest,
  type ResendHandbookRequest,
  type ResendHandbookResponse,
} from './schemas';

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

function getApiBaseUrl() {
  return (import.meta.env.VITE_CLOUD_RUN_URL ?? '').replace(/\/$/, '');
}

function getApiBaseLabel() {
  return getApiBaseUrl() || 'the configured Cloud Run URL';
}

function describeEmptyResponse(response: Response) {
  if (response.status >= 500) {
    return `The API at ${getApiBaseLabel()} returned no JSON response (HTTP ${response.status}). This usually means Cloud Run crashed before replying, or a required server env var like STRIPE_SECRET_KEY is missing.`;
  }
  if (response.status === 404) {
    return `The API route was not found at ${getApiBaseLabel()}. Check that VITE_CLOUD_RUN_URL points to the latest backend deployment.`;
  }
  if (response.status === 0) {
    return `No response came back from ${getApiBaseLabel()}. Check that the backend is reachable from this app.`;
  }
  return `The API at ${getApiBaseLabel()} returned an empty response (HTTP ${response.status}). Check that the backend is running the latest pay-first flow.`;
}

function describeInvalidResponse(response: Response) {
  const contentType = response.headers.get('content-type') || 'unknown content-type';
  return `The API at ${getApiBaseLabel()} returned an invalid response (${contentType}, HTTP ${response.status}). This often means the request hit the wrong service or a server error page instead of the JSON API.`;
}

async function readJsonPayload(response: Response) {
  const text = await response.text();
  if (!text.trim()) {
    throw new ApiError(describeEmptyResponse(response), response.status || 500);
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new ApiError(describeInvalidResponse(response), response.status || 500);
  }
}

async function postJson<T>(
  path: string,
  body: unknown,
  parse: (payload: unknown) => T,
) {
  const idToken = await getCurrentIdToken();
  if (!idToken) {
    throw new Error('You must be signed in to continue.');
  }

  let response: Response;
  try {
    response = await fetch(`${getApiBaseUrl()}${path}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch (error) {
    throw new ApiError(
      `Could not reach the API at ${getApiBaseLabel()}. Check VITE_CLOUD_RUN_URL, CORS, and whether Cloud Run is deployed and reachable.`,
      0,
    );
  }

  const payload = await readJsonPayload(response);
  if (!response.ok) {
    throw new ApiError(payload.error ?? 'Request failed', response.status);
  }

  return parse(payload);
}

async function getJson<T>(path: string, parse: (payload: unknown) => T) {
  const idToken = await getCurrentIdToken();
  if (!idToken) {
    throw new Error('You must be signed in to continue.');
  }

  let response: Response;
  try {
    response = await fetch(`${getApiBaseUrl()}${path}`, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
  } catch (error) {
    throw new ApiError(
      `Could not reach the API at ${getApiBaseLabel()}. Check VITE_CLOUD_RUN_URL, CORS, and whether Cloud Run is deployed and reachable.`,
      0,
    );
  }
  const payload = await readJsonPayload(response);
  if (!response.ok) {
    throw new ApiError(payload.error ?? 'Request failed', response.status);
  }

  return parse(payload);
}

export async function createPaymentIntent(input: PaymentIntentRequest) {
  const payload = paymentIntentRequestSchema.parse(input);
  return postJson('/api/payments/intent', payload, paymentIntentResponseSchema.parse);
}

export async function getEntitlementStatus(
  childId: string,
  productId: string,
): Promise<EntitlementStatusResponse> {
  try {
    return await getJson(`/api/entitlements/${childId}/${productId}`, entitlementStatusResponseSchema.parse);
  } catch (error) {
    if (error instanceof Error && error.message === 'Entitlement not found') {
      return entitlementStatusResponseSchema.parse({
        status: 'none',
        emailedAt: null,
        paidAt: null,
      });
    }
    throw error;
  }
}

export async function generateHandbook(
  childId: string,
  productId: string,
  answers: HandbookAnswers,
): Promise<GenerateHandbookResponse> {
  const payload: GenerateHandbookRequest = generateHandbookRequestSchema.parse({
    childId,
    productId,
    answers: handbookAnswersSchema.parse(answers),
  });
  return postJson('/api/generate', payload, generateHandbookResponseSchema.parse);
}

export async function resendHandbookEmail(
  childId: string,
  productId: string,
): Promise<ResendHandbookResponse> {
  const payload: ResendHandbookRequest = resendHandbookRequestSchema.parse({ childId, productId });
  return postJson('/api/entitlements/resend', payload, resendHandbookResponseSchema.parse);
}
